import RefreshIcon from '@mui/icons-material/Refresh'
import { Box, IconButton, Tooltip } from '@mui/material'
import { useIsFetching } from '@tanstack/react-query'
import { useCallback, useEffect, useState } from 'react'

interface ReportRefreshButtonProps {
  onRefresh: () => Promise<unknown>
}

export const ReportRefreshButton: React.FC<ReportRefreshButtonProps> = ({ onRefresh }) => {
  const [isRefreshing, setIsRefreshing] = useState(false)
  const isFetching = useIsFetching({ queryKey: ['analytics'] }) > 0
  const busy = isRefreshing || isFetching

  const handleRefresh = useCallback(async () => {
    setIsRefreshing(true)
    try {
      await onRefresh()
    } finally {
      setIsRefreshing(false)
    }
  }, [onRefresh])

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'F5' || ((event.ctrlKey || event.metaKey) && event.key === 'r')) {
        event.preventDefault()
        handleRefresh()
      }
    }

    document.addEventListener('keydown', handleKeyDown)

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [handleRefresh])

  return (
    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
      <Tooltip title="Refresh this report">
        <IconButton onClick={handleRefresh} disabled={busy} size="large">
          <RefreshIcon
            sx={{
              animation: busy ? 'spin 1s linear infinite' : 'none',
              '@keyframes spin': {
                '0%': { transform: 'rotate(0deg)' },
                '100%': { transform: 'rotate(360deg)' },
              },
            }}
          />
        </IconButton>
      </Tooltip>
    </Box>
  )
}

export default ReportRefreshButton
